import { usePopover, Overlay } from '@react-aria/overlays';
import React__default from 'react';
import { useSwanPopover } from './popover.context.js';
import { useInternalDialogConfigContext } from '../modal-dialog/dialog.component.js';
import { useIsomorphicLayoutEffect } from '../../hooks/use-isomorphic-layout-effect.js';
import { renderWithRef } from '../core/render-with-ref.js';
import { RenderComp } from '../core/render-comp.component.js';
import { assignRefs } from '../../../core/utilities/ref.utils.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
const propKeysToRemove = [
    'placement',
    'offset',
    'isNonModal',
    'hideArrow',
    'fullBleed',
    'portalContainer',
    'labelText'
];
/**
 * No a public component, has base functionality to be used by listbox, combobox, and popover
 */ const PopoverContentInternal = renderWithRef('PopoverContent', {}, (props, ref)=>{
    const { children , offset =8 , isNonModal , portalContainer: portalContainerProp , hideArrow =false , fullBleed , className , placement ='top'  } = props;
    const { popoverRef , triggerRef , overlayProps , overlayState  } = useSwanPopover();
    const { inModal , portalRef , dialogRef  } = useInternalDialogConfigContext();
    const portalContainer = portalContainerProp !== null && portalContainerProp !== void 0 ? portalContainerProp : inModal && portalRef.current ? portalRef.current : undefined;
    const { popoverProps , underlayProps , placement: calculatedPlacement  } = usePopover({
        popoverRef,
        triggerRef,
        placement,
        offset,
        isNonModal,
        // the popover will be absolutely positioned relative to the dialog element if it's in a modal
        boundaryElement: inModal && dialogRef.current || undefined
    }, overlayState);
    const combinedRef = assignRefs(ref, popoverRef);
    const classes = new Set([
        'swan-popover',
        getPopoverArrowClassName(calculatedPlacement, hideArrow)
    ]);
    if (fullBleed) classes.add('swan-popover-skin-full-bleed');
    if (className) classes.add(className);
    useIsomorphicLayoutEffect(()=>{
        if (!overlayState.isOpen || hideArrow) return;
        if (triggerRef.current && popoverRef.current) {
            const triggerDomRect = triggerRef.current.getBoundingClientRect();
            const contentRect = popoverRef.current.getBoundingClientRect();
            // determine if the arrow is outside of the x/y position of the trigger
            // an arrow pointing at nothing is ugly
            let min = 0, center = 0, max = 0;
            if (calculatedPlacement === 'top' || calculatedPlacement === 'bottom') {
                min = triggerDomRect.left;
                max = triggerDomRect.right;
                center = contentRect.left + contentRect.width / 2;
            } else {
                min = triggerDomRect.top;
                max = triggerDomRect.bottom;
                center = contentRect.top + contentRect.height / 2;
            }
            if (center < min || center > max) {
                popoverRef.current.classList.add('swan-popover-hide-arrow');
            }
        }
    }, [
        overlayState.isOpen,
        hideArrow,
        popoverRef,
        triggerRef,
        calculatedPlacement
    ]);
    return /*#__PURE__*/ React__default.createElement(Overlay, _object_spread_props(_object_spread({}, overlayProps), {
        portalContainer: portalContainer
    }), !isNonModal && /*#__PURE__*/ React__default.createElement("div", underlayProps), /*#__PURE__*/ React__default.createElement(RenderComp, {
        root: "div",
        forwardedRef: combinedRef,
        props: _object_spread_props(_object_spread({}, props, popoverProps), {
            'aria-label': props.labelText
        }),
        propKeysToRemove: propKeysToRemove,
        classNames: classes
    }, children));
});
// todo: remove the arrow if the placement does not align with the center of the trigger
function getPopoverArrowClassName(placement, hideArrow = false) {
    if (hideArrow) {
        return 'swan-popover-hide-arrow';
    }
    const positions = [
        'top',
        'right',
        'bottom',
        'left'
    ];
    if (placement && positions.indexOf(placement) >= 0) {
        return `swan-popover-position-${placement}`;
    }
    // react-aria has more positions than our CSS supports, so hide the arrow if its not one of the 4
    return 'swan-popover-hide-arrow';
}

export { PopoverContentInternal };
